import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Trophy } from "lucide-react";
import { sectionBadgeColor, sectionLabel } from "@/components/RuleComponents";
import { PdfPagesViewer } from "@/components/PdfPagesViewer";
import { LinkifiedText } from "@/components/LinkifiedText";
import { useNav, VIEWS } from "@/lib/nav";

export function SteamrollerPage() {
  const { navigate } = useNav();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .listSteamrollerSections()
      .then((d) => setSections(d.sections))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="grid gap-3" data-testid="steamroller-page">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-5 h-5 text-amber-400" />
        <h2 className="text-lg font-semibold">Steamroller 2026 — Organized Play</h2>
        <Badge variant="outline" className="text-amber-300 border-amber-700/60 ml-auto">
          Tournament rules
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground -mt-2 mb-2">
        Scenarios, scoring, tournament procedure. Re-uploaded yearly via Admin.
      </p>
      {loading && (
        <>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </>
      )}
      {!loading && sections.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            No Steamroller document loaded. Upload one from the Admin tab.
          </CardContent>
        </Card>
      )}
      {sections.map((s) => (
        <Card
          key={s.id}
          className="cursor-pointer transition hover:border-amber-700/60 hover:bg-card/80"
          onClick={() => navigate(VIEWS.steamrollerSection(s.id))}
          data-testid={`steamroller-section-card-${s.id}`}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className="text-amber-300 border-amber-700/60 shrink-0">
                  {s.chapter}
                </Badge>
                <h3 className="font-semibold truncate">{s.title}</h3>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {s.pages.length === 1
                  ? `p${s.pages[0]}`
                  : `pp${s.pages[0]}–${s.pages[s.pages.length - 1]}`}
                {" · "}
                {Math.round(s.char_count / 1000)}k chars
              </span>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap">
              {s.excerpt}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function SteamrollerSectionDetail({ id }) {
  const { navigate } = useNav();
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(null);

  useEffect(() => {
    setLoading(true);
    setActivePage(null);
    api.getSteamrollerSection(id).then((d) => {
      setSection(d);
      setLoading(false);
    });
  }, [id]);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground" data-testid="steamroller-section-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading section…
      </div>
    );
  if (!section) return <div>Not found.</div>;

  return (
    <div className="space-y-4" data-testid="steamroller-section-detail">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-amber-300 border-amber-700/60">
              {section.chapter}
            </Badge>
            <h2 className="text-xl font-semibold" data-testid="steamroller-section-title">{section.title}</h2>
            <Badge variant="outline" className="text-[10px] text-muted-foreground ml-1">
              Steamroller
            </Badge>
            <span className="text-xs text-muted-foreground ml-auto">
              PDF pages {section.pages[0]}{section.pages.length > 1 ? `–${section.pages[section.pages.length - 1]}` : ""} · {section.char_count.toLocaleString()} chars
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid xl:grid-cols-[1.1fr,1fr] gap-4">
        <Card data-testid="steamroller-pdf-panel">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-muted-foreground">
              <Trophy className="w-3.5 h-3.5 text-amber-400" /> Steamroller PDF page
            </div>
            <PdfPagesViewer
              pages={section.pages}
              chapter={section.chapter}
              title={section.title}
              source="steamroller"
              activePage={activePage}
            />
            <p className="text-[11px] text-muted-foreground mt-2 italic">
              Scenario diagrams and element placement are preserved from the source PDF.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                Extracted text
              </div>
              <ScrollArea className="h-[55vh] pr-3" data-testid="steamroller-section-text">
                <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-foreground/90">
                  <LinkifiedText
                    text={section.text}
                    source="steamroller"
                    currentPages={section.pages}
                    onJumpInside={setActivePage}
                    pdfTotalPages={section.pdf_total_pages}
                  />
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-1 flex items-center gap-2 text-sm">
                <Trophy className="w-4 h-4 text-amber-400" /> Card rules that touch this scenario
                <Badge variant="outline" className="ml-1">{section.related_card_rules.length}</Badge>
              </h3>
              <ScrollArea className="h-[32vh] pr-2">
                <div className="space-y-1.5" data-testid="steamroller-related-rules">
                  {section.related_card_rules.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">
                      No keyword cross-references found.
                    </p>
                  )}
                  {section.related_card_rules.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => navigate(VIEWS.rule(r.id))}
                      className="w-full text-left p-2 rounded border border-border hover:border-amber-700/60 hover:bg-card/70 transition"
                      data-testid={`steamroller-related-rule-${r.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate">{r.name}</span>
                        <Badge variant="outline" className={`text-[10px] ${sectionBadgeColor(r.section)}`}>
                          {sectionLabel(r.section)}
                        </Badge>
                      </div>
                      {r.matched_term && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          keyword: <span className="text-amber-300">{r.matched_term}</span>
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
